import type * as GeoJSON from "geojson";

import {
  FillPatternKey,
  FreehandFeatureProperties,
  LineStyleKey,
  RiskFromMetric,
  SceneLayerEntry,
} from "../../types/storyMap";
import { entryLabel } from "./layerEntries";
import {
  effectiveLineColor,
  FREEHAND_DEFAULT_COLOR,
  FREEHAND_DEFAULT_FILL_OPACITY,
  FREEHAND_DEFAULT_WIDTH,
  isHexColor,
} from "./layerStyles";
import type { UnitSystem } from "../../utils/units";
import { RISK_FROM_RAMP as RISK_FROM_CLASS_RAMP, riskFromMetric } from "../layer_components/riskFromStyles";

/**
 * Scene legend model: a flat list of rows derived from the configured layer
 * entries and their effective styles. Pure — shared by the on-map legend
 * card (StoryLegend), the print preview and the PDF builder, so all three
 * agree on what a layer looks like.
 */

export type LegendSwatch =
  | {
      kind: "fill";
      color: string;
      opacity: number;
      pattern?: FillPatternKey;
      /** Outline colour (absent = same as fill). */
      outline?: string;
    }
  | { kind: "line"; color: string; width: number; lineStyle: LineStyleKey }
  | { kind: "point"; color: string }
  | {
      kind: "ramp";
      /** Left→right colour stops. */
      stops: string[];
      /** Labels for the ramp's ends. */
      from: string;
      to: string;
    };

export interface LegendRow {
  id: string;
  label: string;
  swatch: LegendSwatch;
  /** Sub-rows (vegetation classes, custom layers): rendered indented. */
  children?: { label: string; swatch: LegendSwatch }[];
}

/** Names the legend needs that don't live on the entries themselves. */
export interface LegendContext {
  /** Unit system the ramp bounds are labelled in (frozen stories: the publisher's). */
  unitSystem?: UnitSystem;
  /** Custom layer id → {name, color, opacity} (frozen or live). */
  customLayers?: Record<
    string,
    { name?: string | null; color?: string | null; opacity?: number | null }
  >;
  /** Fire history features on the map (for the year range labels). */
  fireHistory?: GeoJSON.FeatureCollection | null;
  /** Strategy id → name (authed builder; frozen stories have none). */
  strategyNames?: Record<string, string>;
  /**
   * Project id → name. When a stack cites several projects, rows are
   * prefixed with the project's name so the reader can tell them apart.
   */
  projectNames?: Record<string, string>;
}

// Built-in symbology, mirrored from storyLayers.ts paints.
const BOUNDARY = { color: "#00d4ad", width: 2.5 };
const PROPERTIES = { color: "#388E3C", width: 3 };
const TREATMENTS = { color: "#ffb74d", fillOpacity: 0.3, width: 1.5 };
const ASSETS = { color: "#aaff00", fillOpacity: 0.5 };
const IGNITIONS = { color: "#73ff00", fillOpacity: 0.75, width: 5 };
const CUSTOM_FALLBACK = { color: "#1abc9c", opacity: 0.4 };
const INSPECTION = { color: "#ffff00", fillOpacity: 0.6 };
/** Matplotlib "plasma" (collate_results.py colours the burn-likelihood PNG with it). */
const RISK_TO_RAMP = ["#0d0887", "#7e03a8", "#cc4778", "#f89540", "#f0f921"];
/** Same stops as FUEL_FILL_COLOR (src/constants/fuelLoadPaint.ts). */
export const FUEL_RAMP = ["#2e7d32", "#8bc34a", "#fff176", "#e53935", "#b71c1c"];
/** Canopy green of the 3D fuel stand's legend swatch. */
const FUEL_3D_SWATCH = "#4f7a3a";
/** Same classes as the project page's Risk From layer (riskFromStyles). */
export const RISK_FROM_RAMP: string[] = [...RISK_FROM_CLASS_RAMP];

/** Ramp swatch labelled with the metric's class bounds ("0 → 400+ ha"). */
function riskFromSwatch(metric: RiskFromMetric, unitSystem: UnitSystem): LegendSwatch {
  const def = riskFromMetric(metric, unitSystem);
  const unit = metric === "impacted_assets" ? "assets" : unitSystem === "imperial" ? "acres" : "ha";
  return {
    kind: "ramp",
    stops: RISK_FROM_RAMP,
    from: def.ticks[0],
    to: `${def.ticks[def.ticks.length - 1]} ${unit}`,
  };
}

function fillSwatch(
  entry: SceneLayerEntry,
  defaults: { color: string; fillOpacity: number },
  allowColor = true
): LegendSwatch {
  const style = entry.style;
  const color =
    allowColor && isHexColor(style?.color) ? style.color : defaults.color;
  const outline = effectiveLineColor(style);
  return {
    kind: "fill",
    color,
    opacity: style?.fillOpacity ?? defaults.fillOpacity,
    pattern: style?.fillPattern && style.fillPattern !== "none" ? style.fillPattern : undefined,
    outline: outline && outline !== color ? outline : undefined,
  };
}

function lineSwatch(
  entry: SceneLayerEntry,
  defaults: { color: string; width: number }
): LegendSwatch {
  const style = entry.style;
  return {
    kind: "line",
    color: effectiveLineColor(style) ?? defaults.color,
    width: style?.lineWidth ?? defaults.width,
    lineStyle: style?.lineStyle ?? "solid",
  };
}

/** Inclusive [min, max] EndDate years of dated fire-history features. */
export function fireHistoryYearRange(
  fc: GeoJSON.FeatureCollection | null | undefined
): [number, number] | null {
  if (!fc) return null;
  let min = Infinity;
  let max = -Infinity;
  fc.features.forEach((f) => {
    const props = f?.properties ?? {};
    if (props.DateMissing || !props.EndDate) return;
    const year = new Date(props.EndDate).getFullYear();
    if (!Number.isFinite(year) || year < 1910) return;
    min = Math.min(min, year);
    max = Math.max(max, year);
  });
  return Number.isFinite(min) ? [min, max] : null;
}

/** Fire-history hue ramp (decorateFireHistory): red = oldest → green = newest. */
const FIRE_HISTORY_RAMP = ["hsl(0,100%,50%)", "hsl(30,100%,50%)", "hsl(60,100%,50%)", "hsl(90,100%,50%)", "hsl(120,100%,50%)"];

function freehandRows(entry: SceneLayerEntry): LegendRow[] {
  const features = entry.features?.features ?? [];
  if (features.length === 0) return [];
  const label = entryLabel(entry);
  const swatchFor = (f: GeoJSON.Feature): LegendSwatch => {
    const props = (f.properties ?? {}) as FreehandFeatureProperties;
    const color = isHexColor(props.color) ? props.color : FREEHAND_DEFAULT_COLOR;
    const isPolygon =
      f.geometry?.type === "Polygon" || f.geometry?.type === "MultiPolygon";
    if (isPolygon) {
      return {
        kind: "fill",
        color,
        opacity: props.fillOpacity ?? FREEHAND_DEFAULT_FILL_OPACITY,
        outline: color,
      };
    }
    return {
      kind: "line",
      color,
      width: props.lineWidth ?? FREEHAND_DEFAULT_WIDTH,
      lineStyle: props.lineStyle ?? "solid",
    };
  };
  // Labelled features each get a row (deduped by label + look); unlabelled
  // ones collapse into a single row under the layer's name.
  const rows: LegendRow[] = [];
  const seen = new Set<string>();
  let unlabelled: GeoJSON.Feature | null = null;
  features.forEach((f, i) => {
    const props = (f.properties ?? {}) as FreehandFeatureProperties;
    const text = props.label?.trim();
    if (!text) {
      unlabelled = unlabelled ?? f;
      return;
    }
    const swatch = swatchFor(f);
    const key = `${text}|${JSON.stringify(swatch)}`;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({ id: `${entry.id}-${i}`, label: text, swatch });
  });
  if (unlabelled && rows.length === 0) {
    rows.push({ id: entry.id, label, swatch: swatchFor(unlabelled) });
  }
  return rows;
}

/** Legend rows for the entries, in the given order; hidden entries skipped. */
export function buildLegendRows(
  entries: SceneLayerEntry[],
  ctx: LegendContext = {}
): LegendRow[] {
  const rows: LegendRow[] = [];
  const cited = new Set(
    entries.map((e) => e.projectId).filter((p): p is number => p !== null && p !== undefined)
  );
  const multiProject = cited.size > 1;
  entries.forEach((entry) => {
    if (entry.hideFromLegend) return;
    const projectName =
      multiProject && entry.projectId != null ? ctx.projectNames?.[String(entry.projectId)] : undefined;
    const label = projectName ? `${projectName} · ${entryLabel(entry)}` : entryLabel(entry);
    switch (entry.key) {
      case "boundary":
        rows.push({ id: entry.id, label, swatch: lineSwatch(entry, BOUNDARY) });
        break;
      case "properties":
        rows.push({ id: entry.id, label, swatch: lineSwatch(entry, PROPERTIES) });
        break;
      case "treatments": {
        const strategy =
          entry.strategyId != null
            ? ctx.strategyNames?.[String(entry.strategyId)]
            : undefined;
        const text =
          entry.label?.trim() || (strategy ? `${label} · ${strategy}` : label);
        const fill = fillSwatch(entry, TREATMENTS);
        rows.push({
          id: entry.id,
          label: text,
          swatch:
            fill.kind === "fill"
              ? { ...fill, outline: effectiveLineColor(entry.style) ?? TREATMENTS.color }
              : fill,
        });
        break;
      }
      case "assets":
        rows.push({ id: entry.id, label, swatch: fillSwatch(entry, ASSETS) });
        break;
      case "fuel_load":
        rows.push({
          id: entry.id,
          label,
          swatch: { kind: "ramp", stops: FUEL_RAMP, from: "0.5 t/ha", to: "25+ t/ha" },
        });
        break;
      case "fuel_3d":
        rows.push({
          id: entry.id,
          label,
          swatch: { kind: "fill", color: FUEL_3D_SWATCH, opacity: 1 },
        });
        break;
      case "vegetation": {
        const items = entry.legendItems ?? [];
        rows.push({
          id: entry.id,
          label,
          swatch: { kind: "fill", color: "#cccccc", opacity: entry.style?.fillOpacity ?? 0.75 },
          children: items.map((item) => ({
            label: item.label,
            swatch: {
              kind: "fill",
              color: item.color,
              opacity: entry.style?.fillOpacity ?? 0.75,
            },
          })),
        });
        break;
      }
      case "fire_history": {
        const range = fireHistoryYearRange(ctx.fireHistory);
        rows.push({
          id: entry.id,
          label,
          swatch: {
            kind: "ramp",
            stops: FIRE_HISTORY_RAMP,
            from: range ? String(range[0]) : "Oldest",
            to: range ? String(range[1]) : "Newest",
          },
        });
        break;
      }
      case "ignitions":
        rows.push({ id: entry.id, label, swatch: fillSwatch(entry, IGNITIONS) });
        break;
      case "custom_layers": {
        const ids = entry.customLayerIds ?? [];
        const override = isHexColor(entry.style?.color) ? entry.style.color : null;
        const children = ids.map((id) => {
          const meta = ctx.customLayers?.[String(id)];
          const color = override ?? (isHexColor(meta?.color) ? meta.color : CUSTOM_FALLBACK.color);
          return {
            label: meta?.name || `Layer ${id}`,
            swatch: {
              kind: "fill" as const,
              color,
              opacity: entry.style?.fillOpacity ?? meta?.opacity ?? CUSTOM_FALLBACK.opacity,
              pattern:
                entry.style?.fillPattern && entry.style.fillPattern !== "none"
                  ? entry.style.fillPattern
                  : undefined,
            },
          };
        });
        if (children.length === 1 && !entry.label?.trim()) {
          rows.push({ id: entry.id, label: children[0].label, swatch: children[0].swatch });
        } else if (children.length > 0) {
          rows.push({ id: entry.id, label, swatch: children[0].swatch, children });
        }
        break;
      }
      case "risk_from":
        rows.push({
          id: entry.id,
          label:
            entry.label?.trim() ||
            (entry.riskFromMetric === "impacted_assets"
              ? "Risk from · impacted assets"
              : "Risk from · fire size"),
          swatch: riskFromSwatch(entry.riskFromMetric ?? "fire_size", ctx.unitSystem ?? "metric"),
        });
        break;
      case "risk_to":
        rows.push({
          id: entry.id,
          label: entry.label?.trim() || "Risk to · burn likelihood",
          swatch: { kind: "ramp", stops: RISK_TO_RAMP, from: "Low", to: "High" },
        });
        break;
      case "inspection":
        rows.push({
          id: entry.id,
          label: entry.label?.trim() || "Ignitions reaching the point",
          swatch: fillSwatch(entry, INSPECTION, false),
        });
        break;
      case "freehand":
        rows.push(...freehandRows(entry));
        break;
      case "weather":
        rows.push({
          id: entry.id,
          label: entry.label?.trim() || "Wind, temperature & humidity",
          swatch: { kind: "line", color: "#ffffff", width: 2, lineStyle: "dotted" },
        });
        break;
    }
  });
  return rows;
}

/** Rows from several entry lists (compare sides), deduped by label + look. */
export function mergeLegendRows(...lists: LegendRow[][]): LegendRow[] {
  const out: LegendRow[] = [];
  const seen = new Set<string>();
  lists.flat().forEach((row) => {
    const key = `${row.label}|${JSON.stringify(row.swatch)}|${JSON.stringify(row.children ?? null)}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(row);
  });
  return out;
}
